<script setup lang="ts">
/** "Can X do Y on Z?" — check a decision before someone hits a wall. */
import { CircleCheck, CircleX, FlaskConical } from "@lucide/vue";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { simulateAccess, useDatasources, usePrincipals, useRoles } from "@/api/admin";
import type { SimulationResult } from "@/api/contracts";
import AdminNav from "@/components/admin/AdminNav.vue";
import { accessTabs } from "@/components/admin/accessTabs";
import CapabilityPicker from "@/components/admin/CapabilityPicker.vue";
import WhereChips from "@/components/admin/WhereChips.vue";
import FormError from "@/components/common/FormError.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";

definePage({ meta: { title: "Access simulator", requires: { adminArea: "access" } } });

const route = useRoute();
const { can } = useSession();
const principals = usePrincipals("", () => can("admin.users"));
const datasources = useDatasources(() => can("admin.datasources"));
const roles = useRoles();

const user = ref(typeof route.query.user === "string" ? route.query.user : "");
const capability = ref("");
const target = ref<string | undefined>();
const datasource = ref<string | undefined>();
const lineage = ref("");
const environment = ref<string | undefined>();
const sensitivity = ref<string | undefined>();
const busy = ref(false);
const error = ref<unknown>(null);
const result = ref<SimulationResult | null>(null);

const userOptions = computed(() => (principals.data.value ?? []).map((p) => ({ value: p.username, label: p.displayName || p.username, hint: p.email ?? undefined })));
const dsOptions = computed(() => (datasources.data.value ?? []).map((d) => ({ value: `${d.type}/${d.name}`, label: `${d.type}/${d.name}`, hint: d.target ?? undefined })));
const targetOptions = computed(() => [...new Set((datasources.data.value ?? []).map((d) => d.target).filter(Boolean) as string[])].sort());

/** Labels as the server would see them for the chosen datasource or target. */
const labels = computed<Record<string, string>>(() => {
  const out: Record<string, string> = {};
  const ds = datasources.data.value?.find((d) => `${d.type}/${d.name}` === datasource.value);
  if (ds) {
    for (const [k, v] of Object.entries(ds.labels)) if (v) out[k] = v;
    out.datasource = `${ds.type}/${ds.name}`;
    if (ds.target) out.target = ds.target;
  }
  if (target.value) {
    out.target = target.value;
    const [l, e] = target.value.split(":");
    if (l) out.lineage = l;
    if (e) out.environment = e;
  }
  if (lineage.value.trim()) out.lineage = lineage.value.trim();
  if (environment.value) out.environment = environment.value;
  if (sensitivity.value) out.sensitivity = sensitivity.value;
  return out;
});

const grantsById = computed(() => new Map((roles.data.value ?? []).flatMap((r) => r.grants.map((g) => [g.id, { ...g, role: r.label }] as const))));

async function run() {
  if (!user.value.trim() || !capability.value) return;
  busy.value = true;
  error.value = null;
  result.value = null;
  try {
    result.value = await simulateAccess({ user: user.value.trim(), capability: capability.value, labels: Object.keys(labels.value).length ? labels.value : undefined });
  } catch (err) {
    error.value = err;
  } finally {
    busy.value = false;
  }
}

const tabs = computed(() => accessTabs(can));
</script>

<template>
  <div>
    <PageHeader :title="t.nav.access" :description="t.admin.simulatorDescription" :crumbs="[{ label: t.admin.title, to: '/admin' }]">
      <template #tabs><AdminNav :items="tabs" :label="t.nav.access" /></template>
    </PageHeader>

    <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <form class="flex flex-col gap-4 rounded-xl border bg-card p-4 sm:p-5" @submit.prevent="run">
        <div class="grid gap-1.5">
          <Label for="s-user">Can…</Label>
          <SimpleSelect v-if="userOptions.length" id="s-user" v-model="user" :options="userOptions" placeholder="Choose a person" />
          <Input v-else id="s-user" v-model="user" placeholder="username, email or subject" />
        </div>
        <div class="grid gap-1.5">
          <Label for="s-cap">…do</Label>
          <CapabilityPicker id="s-cap" v-model="capability" :allow-globs="false" />
        </div>
        <fieldset class="grid gap-3 rounded-lg border p-3">
          <legend class="px-1 text-sm font-medium">…on <span class="font-normal text-muted-foreground">(optional)</span></legend>
          <div v-if="dsOptions.length" class="grid gap-1.5">
            <Label class="text-xs">Datasource</Label>
            <SimpleSelect v-model="datasource" :options="dsOptions" allow-empty placeholder="Any" aria-label="Datasource" />
          </div>
          <div v-if="targetOptions.length" class="grid gap-1.5">
            <Label class="text-xs">Target</Label>
            <SimpleSelect v-model="target" :options="targetOptions" allow-empty placeholder="Any" aria-label="Target" />
          </div>
          <div class="grid gap-3 sm:grid-cols-3">
            <div class="grid gap-1.5">
              <Label for="s-lineage" class="text-xs">Lineage</Label>
              <Input id="s-lineage" v-model="lineage" placeholder="strauss-eu" />
            </div>
            <div class="grid gap-1.5">
              <Label class="text-xs">Environment</Label>
              <SimpleSelect v-model="environment" :options="['prod', 'staging', 'qa', 'dev', 'shared']" allow-empty placeholder="Any" aria-label="Environment" />
            </div>
            <div class="grid gap-1.5">
              <Label class="text-xs">Sensitivity</Label>
              <SimpleSelect v-model="sensitivity" :options="['public', 'internal', 'confidential', 'pii']" allow-empty placeholder="Any" aria-label="Sensitivity" />
            </div>
          </div>
        </fieldset>
        <FormError :error="error" />
        <Button type="submit" :disabled="busy || !user.trim() || !capability"><FlaskConical /> Check</Button>
      </form>

      <section aria-live="polite" class="flex flex-col gap-4">
        <div v-if="!result" class="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">The answer appears here, with the grants behind it.</div>
        <template v-else>
          <div :class="['flex items-start gap-3 rounded-xl border p-4', result.decision.allowed ? 'border-success/40 bg-success-soft' : 'border-destructive/40 bg-destructive-soft']">
            <component :is="result.decision.allowed ? CircleCheck : CircleX" :class="['size-6 shrink-0', result.decision.allowed ? 'text-success' : 'text-destructive']" aria-hidden="true" />
            <div>
              <p class="font-semibold">{{ result.decision.allowed ? "Yes, allowed" : "No, denied" }}</p>
              <p class="text-sm">{{ result.decision.reason }}</p>
            </div>
          </div>
          <div v-if="Object.keys(labels).length" class="text-sm">
            <p class="eyebrow mb-1">Checked against</p>
            <WhereChips :where="Object.fromEntries(Object.entries(labels).map(([k, v]) => [k, [v]]))" />
          </div>
          <div v-if="result.decision.allowed && Object.keys(result.decision.obligations).length" class="text-sm">
            <p class="eyebrow mb-1">With obligations</p>
            <WhereChips :where="{}" :obligations="result.decision.obligations" />
          </div>
          <div class="text-sm">
            <p class="eyebrow mb-1">Their roles</p>
            <div class="flex flex-wrap gap-1.5">
              <RouterLink v-for="r in result.roles" :key="r" :to="`/admin/access/roles/${r}`"><Badge variant="accent">{{ r }}</Badge></RouterLink>
              <span v-if="result.roles.length === 0" class="text-muted-foreground">none</span>
            </div>
          </div>
          <div v-if="result.decision.grantIds.length" class="text-sm">
            <p class="eyebrow mb-1">Deciding grants</p>
            <ul class="flex flex-col gap-1.5">
              <li v-for="id in result.decision.grantIds" :key="id" class="rounded-md border bg-card px-3 py-2">
                <template v-if="grantsById.get(id)">
                  <code class="font-semibold">{{ grantsById.get(id)!.effect }} {{ grantsById.get(id)!.capability }}</code>
                  <span class="text-muted-foreground"> in {{ grantsById.get(id)!.role }}</span>
                  <WhereChips class="mt-1" :where="grantsById.get(id)!.where" :obligations="grantsById.get(id)!.obligations" />
                </template>
                <span v-else>Grant #{{ id }} (personal grant)</span>
              </li>
            </ul>
          </div>
        </template>
      </section>
    </div>
  </div>
</template>
